"use client";

import { create } from "zustand";
import { migratePick, type HarnessId } from "@bash/shared";
import {
  PICKABLE_MODEL_IDS,
  engineRunsModel,
} from "@/components/ui/model-catalog";
import {
  getModelPreference,
  updateModelPreference,
} from "@/lib/api/modelPreference";

/**
 * Which MODEL id drives the NEXT task (e.g. claude-opus-4-8, gpt-6-luna, glm-5.2).
 * Mirrors runtimeStore/modeStore. The picker reads/writes this; the model is
 * chosen from the SELECTED HARNESS's allowed set (PROVIDERS_BY_HARNESS). The
 * RUNTIME is the harness store's value (the user's explicit choice) — it is NOT
 * derived from the model. When the harness changes, if the current model isn't
 * allowed for the new harness the caller resets it to that harness's default.
 */
/**
 * Pick storage, v2 (founder 08-04: default = Luna for ALL users, legacy included).
 * PICK_KEY is written ONLY by setModel — its presence is trustworthy user intent.
 * LEGACY_KEY is the pre-08-04 store: an old ModelSelector wrote the then-default
 * 'claude-sonnet-5' into it PROGRAMMATICALLY on engine switches, so for that one
 * value presence ≠ intent — every legacy browser reads as a fake permanent sonnet
 * pin. Migration below: any legacy value other than that stamp carries forward as
 * a real pick; the sonnet stamp is purged to the untouched default (a user who
 * truly wants Sonnet re-picks it once, which writes PICK_KEY and sticks forever).
 */
const PICK_KEY = "bash-agent-model-pick";
/** Stored-pick format marker: values written after the 08-25 default change. */
const PICK_V2_PREFIX = "v2:";
const LEGACY_KEY = "bash-agent-model-id";
// A raw stored value, never migrated: the stamp stays purged even though
// migratePick now maps claude-sonnet-5 to its successor (Sonnet 5 retired 2026-09-29).
const LEGACY_POISONED_STAMP = "claude-sonnet-5";
// OFFLINE fallback only — the live fleet default is the server's runtime_config
// row (productDefault on /api/preferences/model), adopted in syncModelPreference.
// Founder 2026-08-25 evening (after the Sol credit burn): back to Luna. Keep this
// in step with RUNTIME_CONFIG_DEFAULTS.default_model on the server.
export const DEFAULT_MODEL = "gpt-6-luna";

// Stored-pick migrations: a delisted model an explicit pick pointed at maps forward
// to its successor (migratePick, @bash/shared) so the pick follows the family
// instead of self-healing to the default. The server applies the same table to the
// account row, so the pill and server-started tasks can never disagree.

/** Read the persisted model + whether it counts as an explicit pick. A currently
 *  pickable id under PICK_KEY = the user picked it (survives reload); anything else
 *  (absent or stale/poisoned) is the untouched default, which stays adaptive. */
function loadModelState(): { model: string; picked: boolean } {
  if (typeof window === "undefined")
    return { model: DEFAULT_MODEL, picked: false };
  const rawPick = localStorage.getItem(PICK_KEY);
  // v2-prefixed = written by a pill tap AFTER the 08-25 default change — always
  // real intent, honored verbatim (a deliberate Luna pick included).
  if (rawPick?.startsWith(PICK_V2_PREFIX)) {
    const v2 = rawPick.slice(PICK_V2_PREFIX.length);
    const mapped = migratePick(v2);
    if (PICKABLE_MODEL_IDS.has(mapped)) return { model: mapped, picked: true };
    localStorage.removeItem(PICK_KEY);
    return { model: DEFAULT_MODEL, picked: false };
  }
  const pick = rawPick && migratePick(rawPick);
  // Any bare (pre-v2) pickable value is honored as a pick and upgraded to v2.
  // The 08-25 "retired Luna" purge is GONE: with Luna the fleet default again it
  // deleted genuine Luna picks (the pill then showed the default while trivial
  // asks rode another ladder rung — the 08-25 evening mismatch). A selection
  // always outranks bookkeeping; the client never deletes what reads as one.
  if (pick && PICKABLE_MODEL_IDS.has(pick)) {
    localStorage.setItem(PICK_KEY, PICK_V2_PREFIX + pick);
    return { model: pick, picked: true };
  }

  // One-time legacy migration — always consumes the old key.
  const rawLegacy = localStorage.getItem(LEGACY_KEY);
  if (rawLegacy) {
    localStorage.removeItem(LEGACY_KEY);
    const legacy = migratePick(rawLegacy);
    if (rawLegacy !== LEGACY_POISONED_STAMP && PICKABLE_MODEL_IDS.has(legacy)) {
      localStorage.setItem(PICK_KEY, PICK_V2_PREFIX + legacy);
      return { model: legacy, picked: true };
    }
  }
  return { model: DEFAULT_MODEL, picked: false };
}

interface ModelState {
  model: string;
  /** True once the user has explicitly picked a model (persisted via the stored id,
   *  so it survives reload). False = the untouched, adaptive default. */
  picked: boolean;
  /** The FLEET default (runtime_config, productDefault on /api/preferences/model),
   *  whatever the person picked: the model a failed task starts again on. The
   *  baked DEFAULT_MODEL until the server says otherwise. */
  productDefault: string;
  setModel: (model: string) => void;
}

export const useModelStore = create<ModelState>((set) => ({
  ...loadModelState(),
  productDefault: DEFAULT_MODEL,
  setModel: (model) => {
    localStorage.setItem(PICK_KEY, PICK_V2_PREFIX + model);
    localStorage.removeItem(LEGACY_KEY);
    set({ model, picked: true });
    // Account-level home (founder 08-04): the pick rides to the server so it
    // follows the user across devices and into server-minted turns (routines).
    // Best-effort — offline just means this device keeps its local cache until
    // the next successful sync.
    void updateModelPreference(model).catch(() => {});
  },
}));

/**
 * One-shot per page load: reconcile the pill with the ACCOUNT default.
 * Server value present → it wins (localStorage becomes a cache of it — the
 * server is the truth, the browser is the hint). Server empty but this browser
 * holds a real pick → upload it once, seeding the account from the strongest
 * signal we have. Offline/error → the local cache governs this session.
 */
let modelPreferenceSynced = false;
export async function syncModelPreference(): Promise<void> {
  if (modelPreferenceSynced) return;
  modelPreferenceSynced = true;
  try {
    const { defaultModel, productDefault } = await getModelPreference();
    if (productDefault && PICKABLE_MODEL_IDS.has(productDefault))
      useModelStore.setState({ productDefault });
    if (defaultModel) {
      const mapped = migratePick(defaultModel);
      // An account row IS a pick (only pill taps write it since 08-25; the
      // luna-era echo rows were healed server-side during train 10). The 08-25
      // client-side Luna heal is gone with the purge above — it PUT null over
      // rows that were real Luna picks made on another device.
      if (PICKABLE_MODEL_IDS.has(mapped)) {
        localStorage.setItem(PICK_KEY, PICK_V2_PREFIX + mapped);
        localStorage.removeItem(LEGACY_KEY);
        useModelStore.setState({ model: mapped, picked: true });
        return;
      }
      // Unknown/retired id — treat as no preference.
    }
    // No user preference: the FLEET default (runtime_config, DB-managed)
    // governs the pill. Shown as unpicked — the user's own pick still wins
    // and is never substituted. Falls back to the baked DEFAULT_MODEL when an
    // older server omits the field or ships an unknown id.
    if (productDefault && PICKABLE_MODEL_IDS.has(productDefault)) {
      const local = useModelStore.getState();
      if (!local.picked)
        useModelStore.setState({ model: productDefault, picked: false });
    }
    const local = useModelStore.getState();
    if (local.picked) await updateModelPreference(local.model);
  } catch {
    // Best-effort: the local cache governs this session.
  }
}

/**
 * The (model, runtime) pair a NEW task dispatches with — the single seam both
 * composers (home Dock + task MessageInput) ride via taskStore.sendMessage.
 *
 * - EXPLICIT pick → sent verbatim. The pill is user intent: a model the user chose
 *   is NEVER gated or substituted. (An explicit GLM pick on an image task still goes
 *   verbatim even though GLM's lane is image-blind — that's a separate product
 *   question, not the composer's to override.)
 * - UNTOUCHED default → NO model is sent (founder 08-25, the Luna-echo incident):
 *   the pill's displayed value is a PREVIEW, not intent, and sending it froze the
 *   fleet default out of every web-created task. The server resolves an absent
 *   model through account preference → runtime_config default. Runtime is forced
 *   'native' so an orphaned stale harness value can never dispatch it on the wrong arm.
 * - On Claude Code, the person's own plan (engines T2b), a pick rides only when that
 *   engine can run it (a Claude model). A stored Vestra pick it cannot run sends
 *   nothing, and the server runs the engine's default: the model the pill shows.
 *   On Codex (engines T3) "can run it" is also its own list (ENGINE_MODEL_IDS).
 */
export function resolveComposerModel(args: {
  model: string;
  picked: boolean;
  runtime: HarnessId;
  /** The engine the next task runs on (engines T2b); absent = Vestra's own. */
  engine?: HarnessId;
}): { model: string | undefined; runtime: HarnessId } {
  const engineRunsIt =
    !args.engine ||
    args.engine === "native" ||
    engineRunsModel(args.model, args.engine);
  if (args.picked && engineRunsIt)
    return { model: args.model, runtime: args.runtime };
  return { model: undefined, runtime: "native" };
}
